import React, { useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPuzzlePiece,
    faUserCircle,
    faCheckCircle,
    faCircleHalfStroke,
    faBox,
    faBoxOpen,
    faTrash,
    faStore,
    faCheck,
} from '@fortawesome/free-solid-svg-icons';

const CollectionSetCard = ({
    set,
    isOwner,
    isBulkMode,
    selectedSets,
    toggleBulkSelect,
    selectedSet,
    toggleSelectSet,
    showPrices,
    isWishlist,
    openStatusModal,
    handleRemoveClick,
    moveToCollection,
}) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const isBulkSelected = isBulkMode && selectedSets.has(set.set_num);
    // selectedSet / isCardSelected only applies to wishlist cards (expand behaviour)
    const isCardSelected = !isBulkMode && isWishlist && selectedSet === set.set_num;

    const handleCardClick = () => {
        if (isBulkMode && isOwner) {
            toggleBulkSelect(set.set_num);
        } else if (isOwner && !isWishlist) {
            openStatusModal(set);
        } else {
            toggleSelectSet(set.set_num);
        }
    };

    const handleImageError = (e) => {
        e.target.src = '/images/lego_piece_questionmark.png';
    };

    return (
        <div
            className={`bg-white rounded-xl shadow-sm border overflow-hidden transition-all duration-200 hover:shadow-md
                ${!isWishlist && Number(set.complete) < Number(set.quantity) ? 'border-amber-400' : 'border-gray-200'}
                ${isOwner ? 'cursor-pointer' : ''}
                ${isBulkSelected ? 'ring-2 ring-blue-500 bg-blue-50/30' : ''}
                ${isCardSelected ? 'ring-2 ring-red-500 ring-opacity-50' : ''}`}
            onClick={handleCardClick}
        >
            <div className="relative">
                <div className="p-2 h-48 flex items-center justify-center relative">
                    {!isLoaded && (
                        <div className="absolute inset-2">
                            <Skeleton height="100%" width="100%" />
                        </div>
                    )}

                    {/* Bulk mode checkbox */}
                    {isBulkMode && isOwner && (
                        <div className={`absolute top-2 left-2 z-10 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-150 shadow-sm ${
                            isBulkSelected ? 'bg-blue-500 border-blue-500' : 'bg-white border-gray-400'
                        }`}>
                            {isBulkSelected && <FontAwesomeIcon icon={faCheck} className="text-white text-xs" />}
                        </div>
                    )}

                    <img
                        src={set.img_url}
                        alt={set.name}
                        className={`h-full object-contain max-w-full ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                        onError={handleImageError}
                        onLoad={() => setIsLoaded(true)}
                        style={{ transition: 'opacity 0.3s' }}
                    />

                    {/* Quantity badge */}
                    {!isWishlist && set.quantity > 1 && (
                        <div className="absolute top-2 right-2 bg-gradient-to-br from-amber-400 to-amber-500 text-gray-900 min-h-10 min-w-10 px-3 rounded-full flex items-center justify-center font-extrabold text-base shadow-xl border-2 border-white hover:from-amber-500 hover:to-amber-600 transition-all duration-200 hover:scale-105">
                            ×{set.quantity}
                        </div>
                    )}
                </div>

                {/* Set details */}
                <div className="p-3 sm:p-4 pt-0 bg-gray-50/50">
                    <h3 className="font-medium text-gray-800 mb-1 line-clamp-2 text-sm sm:text-base leading-tight" style={{ minHeight: '2.5rem' }}>
                        {set.name}
                    </h3>

                    <div className="flex justify-between items-center mb-2">
                        <span className="text-xs sm:text-sm text-gray-600 font-mono truncate flex-shrink">{set.set_num}</span>
                        <span className="text-xs sm:text-sm bg-gray-100 px-2 py-0.5 sm:py-1 rounded-lg ml-2 flex-shrink-0">{set.year}</span>
                    </div>

                    <div className="space-y-2 mb-4">
                        {/* Pieces + minifigs row */}
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                            <div className="flex items-center gap-2 flex-wrap">
                                <div className="bg-gradient-to-r from-red-500 to-red-600 text-white text-xs sm:text-sm font-semibold rounded-lg px-2 sm:px-3 py-1 sm:py-1.5 flex items-center shadow-sm border border-red-600/20">
                                    <FontAwesomeIcon icon={faPuzzlePiece} className="mr-1 sm:mr-2 text-red-100" size="sm" />
                                    <span className="whitespace-nowrap">{set.num_parts.toLocaleString()}</span>
                                </div>

                                {set.num_minifigures > 0 && (
                                    <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white border-purple-600/20 text-xs sm:text-sm font-semibold rounded-lg px-2 sm:px-3 py-1 sm:py-1.5 flex items-center shadow-sm border">
                                        <FontAwesomeIcon icon={faUserCircle} className="mr-1 sm:mr-2 opacity-90" size="sm" />
                                        <span className="whitespace-nowrap">
                                            {!isWishlist && set.expected_minifigures > 0
                                                ? `${set.owned_minifigures}/${set.expected_minifigures}`
                                                : set.num_minifigures
                                            }
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Collection status (not shown on wishlist) */}
                        {!isWishlist && (
                            <div className="flex flex-col sm:flex-row items-stretch gap-2">
                                {/* Completion */}
                                <div className="flex-1">
                                    <div className={`text-xs sm:text-sm font-medium rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 flex items-center justify-center shadow-sm border min-h-[2rem] ${
                                        Number(set.complete) === Number(set.quantity)
                                            ? 'bg-gradient-to-r from-emerald-50 to-emerald-100 text-emerald-800 border-emerald-200 shadow-emerald-100'
                                            : Number(set.complete) > 0
                                                ? 'bg-gradient-to-r from-blue-50 to-blue-100 text-blue-800 border-blue-200 shadow-blue-100'
                                                : 'bg-gradient-to-r from-amber-50 to-amber-100 text-amber-800 border-amber-200 shadow-amber-100'
                                    }`}>
                                        <FontAwesomeIcon
                                            icon={Number(set.complete) === Number(set.quantity) ? faCheckCircle : faCircleHalfStroke}
                                            className="mr-1 sm:mr-2 flex-shrink-0"
                                            size="sm"
                                        />
                                        <span className="font-semibold whitespace-nowrap overflow-hidden text-ellipsis">
                                            {Number(set.complete) === Number(set.quantity)
                                                ? 'Complete'
                                                : Number(set.complete) > 0
                                                    ? `${set.complete}/${set.quantity}`
                                                    : 'Incomplete'
                                            }
                                        </span>
                                    </div>
                                </div>

                                {/* Sealed */}
                                <div className="flex-1">
                                    <div className={`text-xs sm:text-sm font-medium rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 flex items-center justify-center shadow-sm border min-h-[2rem] ${
                                        Number(set.sealed) > 0
                                            ? 'bg-gradient-to-r from-blue-50 to-blue-100 text-blue-800 border-blue-200 shadow-blue-100'
                                            : 'bg-gradient-to-r from-slate-50 to-slate-100 text-slate-700 border-slate-200 shadow-slate-100'
                                    }`}>
                                        <FontAwesomeIcon
                                            icon={Number(set.sealed) > 0 ? faBox : faBoxOpen}
                                            className="mr-1 sm:mr-2 flex-shrink-0"
                                            size="sm"
                                        />
                                        <span className="font-semibold whitespace-nowrap overflow-hidden text-ellipsis">
                                            {Number(set.sealed) === Number(set.quantity)
                                                ? 'Sealed'
                                                : Number(set.sealed) > 0
                                                    ? `${set.sealed}/${set.quantity}`
                                                    : 'Opened'
                                            }
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Prices */}
                    {showPrices && (
                        <div className="border-t border-gray-100 pt-2 sm:pt-3 mb-3">
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs sm:text-sm">
                                {set.retail_price && (
                                    <div className="text-center sm:text-left">
                                        <div className="text-xs text-gray-500 mb-0.5">Retail</div>
                                        <div className="font-medium truncate">${Number(set.retail_price).toFixed(2)}</div>
                                    </div>
                                )}
                                {set.sealed_value && (
                                    <div className="text-center sm:text-left">
                                        <div className="text-xs text-gray-500 mb-0.5">Sealed</div>
                                        <div className="font-medium text-green-600 truncate">${Number(set.sealed_value).toFixed(2)}</div>
                                    </div>
                                )}
                                {set.used_value && (
                                    <div className="text-center sm:text-left col-span-2 sm:col-span-1">
                                        <div className="text-xs text-gray-500 mb-0.5">Used</div>
                                        <div className="font-medium text-blue-600 truncate">${Number(set.used_value).toFixed(2)}</div>
                                    </div>
                                )}
                                {set.retail_price && !set.sealed_value && !set.used_value && (
                                    <div className="col-span-2 flex items-center gap-1">
                                        <FontAwesomeIcon icon={faStore} className="text-blue-400 text-xs" />
                                        <span className="text-blue-600 font-medium text-xs">Available in stores</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Wishlist owner actions (shown when card is selected) */}
                    {isOwner && !isBulkMode && isWishlist && isCardSelected && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                            <div className="flex flex-col gap-2">
                                <button
                                    className="py-2 px-3 bg-yellow-400 hover:bg-yellow-500 text-gray-800 rounded-lg font-medium transition-colors shadow-sm flex items-center justify-center"
                                    onClick={e => { e.stopPropagation(); moveToCollection(set.set_num); }}
                                >
                                    <FontAwesomeIcon icon={faBoxOpen} className="mr-2" />
                                    Move to Collection
                                </button>
                                <button
                                    className="py-2 px-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors shadow-sm flex items-center justify-center"
                                    onClick={e => handleRemoveClick(set, e)}
                                >
                                    <FontAwesomeIcon icon={faTrash} className="mr-2" />
                                    <span>Remove</span>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CollectionSetCard;
